/* ==========================================================================
   LUJE — sibling-colour dots on product cards (25 Aug 2026).

   Itsperfect syncs one product per (item, colour), so a card's own swatch
   row shows exactly one dot. This appends the style's OTHER colours as
   linked dots — the card-level counterpart of the PDP sibling picker
   (assets/luje-product-colors.js), sharing its mechanism: products are
   grouped by their Itsperfect `ItemNumber:` tag, looked up through
   Shopify's predictive-search endpoint scoped to the tag field.

   Frugality rules (this store just finished a performance audit):
   - fetch only when a row scrolls within 300px of the viewport;
   - one fetch per unique style tag per SESSION (in-memory + sessionStorage
     cache — pagination, filter re-renders and back-navigation reuse it);
   - siblings that are also on the current page share the same cached
     promise, so a collection page with a 3-colour family costs one request.

   Sold-out siblings are excluded entirely (the site-wide convention:
   sold-out colourways are hidden, not shown disabled). Cached availability
   is at most session-stale; the PDP re-checks live on every view.

   Dots reuse the native card-dot geometry via .luje-card-sibling
   (luje-design.css) — deliberately NOT .product-option-item, which
   app.min.js's initOptions() rescans against THIS product's variant data on
   every variant change and would blank a foreign colour (the same trap the
   PDP sibling swatches dodge). Colour comes from the same
   MinimogSettings.product_colors map the native dots use; an unmapped name
   falls back to the sibling's product photo, so a missing map entry can
   never produce a blank dot.
   ========================================================================== */
(function () {
  'use strict';

  var colourMap = null;
  function map() {
    if (colourMap) return colourMap;
    colourMap = {};
    var raw = (window.MinimogSettings && window.MinimogSettings.product_colors) || '';
    raw.split(',').forEach(function (e) {
      var i = e.indexOf(':');
      if (i > 0) colourMap[e.slice(0, i).trim().toLowerCase()] = e.slice(i + 1).trim();
    });
    return colourMap;
  }

  function colourLabel(title) {
    var p = title.split('|');
    return p.length > 1 ? p[p.length - 1].trim() : title.trim();
  }

  var mem = {};
  function fetchSiblings(tag) {
    if (mem[tag]) return mem[tag];
    var key = 'luje-sib:' + tag;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) { mem[tag] = Promise.resolve(JSON.parse(cached)); return mem[tag]; }
    } catch (e) { /* private mode etc. — fall through to fetch */ }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(tag) +
      '&resources[type]=product' +
      '&resources[limit]=10' +
      '&resources[options][fields]=tag' +
      '&resources[options][unavailable_products]=show';
    mem[tag] = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var ps = (d && d.resources && d.resources.results && d.resources.results.products) || [];
        var slim = ps.map(function (p) {
          /* p.url carries predictive-search tracking params (?_pos=&_psq=...)
             - strip them, they're navigation noise on a plain colour link. */
          return { handle: p.handle, url: (p.url || '').split('?')[0], title: p.title, available: p.available, image: p.image };
        });
        try { sessionStorage.setItem(key, JSON.stringify(slim)); } catch (e) {}
        return slim;
      })
      .catch(function () { return []; });
    return mem[tag];
  }

  function render(row, siblings) {
    siblings.forEach(function (p) {
      var label = colourLabel(p.title);

      var tip = document.createElement('div');
      tip.className = 'sf__tooltip-item sf__tooltip-top';

      var sw = document.createElement('div');
      sw.className = 'sf__variant-swatch flex justify-center items-center';

      var a = document.createElement('a');
      a.className = 'luje-card-sibling flex items-center justify-center';
      /* Stripped at cache-write too, but a session cache written by an older
         version may still carry params - strip at render as well. */
      a.href = (p.url || '').split('?')[0];
      a.setAttribute('aria-label', label);
      var hex = map()[label.toLowerCase()];
      if (hex) a.style.backgroundColor = hex;
      else if (p.image) a.style.backgroundImage = 'url(' + p.image + ')';

      sw.appendChild(a);
      tip.appendChild(sw);

      var tipTxt = document.createElement('span');
      tipTxt.className = 'sf__tooltip-content capitalize';
      tipTxt.textContent = label;
      tip.appendChild(tipTxt);

      row.appendChild(tip);
    });
  }

  function load(row) {
    var tag = row.getAttribute('data-group-tag');
    var current = row.getAttribute('data-current-handle');
    if (!tag) return;
    fetchSiblings(tag).then(function (ps) {
      if (row.dataset.lujeSiblingsRendered) return;
      row.dataset.lujeSiblingsRendered = '1';
      var siblings = ps.filter(function (p) { return p.handle !== current && p.available !== false; });
      if (siblings.length) render(row, siblings);
    });
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          load(en.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  function scan() {
    var rows = document.querySelectorAll('[data-luje-card-colors]:not([data-luje-card-colors-init])');
    for (var i = 0; i < rows.length; i++) {
      rows[i].setAttribute('data-luje-card-colors-init', '1');
      if (io) io.observe(rows[i]); else load(rows[i]);
    }
  }

  var scanTimer = null;
  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 150);
  }

  function boot() {
    scan();
    /* Collection filter/load-more re-renders replace the grid wholesale, and
       the recommendations rail injects its cards after load — watch for new
       UNMARKED rows. Our own sibling appends land inside rows already marked
       -init, so they schedule a scan that finds nothing: no self-trigger
       loop (the luje-cart-goal lesson), and the debounced callback itself is
       one cheap querySelectorAll. */
    new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
